/**
 * `ChatLevelChip` — the composer's level chip (P4). Absorbs `ModelBadge`'s
 * slot; click opens the pick popover IN PLACE (no `/settings` redirect —
 * operator design ruling 2026-08-29, miniplan §2-bis).
 *
 * SHORT format, iteration 2 (operator, 2026-08-29 late): `<model>` + mode
 * ICON ONLY — the mode word moved into the popover; the icon carries the
 * mode on the badge. When no session pick exists, the icon shown is where
 * the ORG DEFAULT lands (unique model match, `defaultMatchLevel`) — the
 * popover marks the current setting with the CHECK icon (iteration 3 —
 * Claude-picker idiom), pick or default alike; data attributes keep the
 * pick/default distinction for probes.
 *
 * Takes the `useChatLevel` bundle as a PROP (one hook instance per composer,
 * owned by `ChatInput`) so a definitive failure from a pick reaches the same
 * instance the L-9 gate reads.
 *
 * Honesty rules:
 *  - `data-level` carries the SESSION pick only (null in the default state)
 *    — probes read the truth even where the pixels compress it;
 *  - an AMBIGUOUS default match (two levels on one model) shows NO icon;
 *  - `unknown` (the read-back call failed) shows the unknown copy, no model
 *    claim, and chat stays usable (miniplan §3(b)).
 */
import { AlertCircle, ChevronDown, Hourglass, Rocket, Scale } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import type { useChatLevel } from "@/hooks/useChatLevel";
import {
  chipFullRef,
  defaultMatchLevel,
  levelLabelKey,
  profileNameThinkingSuffix,
  type ConciergeMode,
} from "@/lib/chat-level";
import { ChatLevelDialog } from "./ChatLevelDialog";

const MODE_ICONS: Record<ConciergeMode, typeof Rocket> = {
  FAST: Rocket,
  NORMAL: Scale,
  DEEP: Hourglass,
};

export function ChatLevelChip({ level: bundle }: { level: ReturnType<typeof useChatLevel> }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { state, rows, busy, fetchRows, pick, notApplied } = bundle;

  // Rows are needed EAGERLY in the default state (the badge icon derives from
  // the default→level match); the fetch is a pure in-memory read server-side.
  const needRows = state?.kind === "org-default" && rows === null;
  useEffect(() => {
    if (needRows) void fetchRows();
  }, [needRows, fetchRows]);

  if (state === null) return null; // loading — fail-soft, like ModelBadge

  // What the chip shows, per state (§2-bis.c, iteration 2).
  let model: string | null = null;
  let pickLevel: ConciergeMode | null = null;
  let degraded = false;
  if (state.kind === "session-level") {
    model = state.resolved?.model ?? null;
    pickLevel = state.level;
  } else if (state.kind === "org-default") {
    model = state.resolved?.model ?? state.profileName;
  } else if (state.kind === "unavailable") {
    degraded = true;
  }
  const defaultMatch = defaultMatchLevel(state, rows);
  const iconLevel = pickLevel ?? defaultMatch; // badge compresses; popup is exact
  const Icon = iconLevel ? MODE_ICONS[iconLevel] : null;

  // Tooltip: "<Level>: provider/model · thinking" when a level is marked
  // (operator, 2026-08-30: the tooltip reflects the selection — e.g.
  // "Rápido: openai-codex/gpt-5.6-luna · minimal"); the plain full ref when
  // none is (operator, 2026-08-29: "full model name as a tooltip"). Pieces
  // are server-given only: the read-back's resolved ref, the profile NAME's
  // declared ":suffix", and the marked level's own `getLevels` row — the
  // pre-roll org-default ref carries no thinking, the row does.
  const refObj =
    state.kind === "session-level" || state.kind === "org-default" ? state.resolved : null;
  const nameSuffix =
    state.kind === "org-default" ? profileNameThinkingSuffix(state.profileName) : null;
  const iconRow = iconLevel ? (rows?.find((r) => r.level === iconLevel) ?? null) : null;
  const ref = chipFullRef(refObj, nameSuffix, iconRow?.resolved ?? null);
  const fullRef = ref
    ? iconLevel
      ? `${t(levelLabelKey(iconLevel))}: ${ref}`
      : ref
    : undefined;

  // A read that is still being retried is NOT the same statement as a read that
  // settled with nothing to show (i-ter, 2026-09-12). Collapsing the two is how
  // a page loaded into a container's boot tail ended up claiming the level was
  // unavailable when it simply had not answered yet.
  const retrying = state.kind === "unknown" && state.retrying === true;
  const label = degraded
    ? t("chat.level.gateShort")
    : retrying
      ? t("chat.level.checking")
      : model === null && iconLevel === null
        ? t("chat.level.unknown")
        : (model ?? "");

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) void fetchRows(); // refresh rows on open (cheap, in-memory)
  };

  return (
    <div className="relative shrink-0">
      <button
        type="button"
        data-testid="chat-level-chip"
        data-level={pickLevel ?? undefined}
        data-default-level={!pickLevel && defaultMatch ? defaultMatch : undefined}
        data-degraded={degraded ? "true" : undefined}
        onClick={toggle}
        title={fullRef}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={
          iconLevel
            ? t(pickLevel ? "chat.level.chipAria" : "chat.level.chipAriaDefault", {
                level: t(levelLabelKey(iconLevel)),
              })
            : t("chat.level.chipAriaNoLevel", { model: model ?? "?" })
        }
        className={`flex items-center gap-1 truncate rounded-md px-1.5 py-0.5 text-[11px] font-normal transition-colors ${
          degraded
            ? "text-red-600 hover:bg-red-50 hover:text-red-700 dark:text-red-400 dark:hover:bg-red-950/40"
            : "text-coffee-500 hover:bg-cream-100/60 hover:text-coffee-600 dark:text-cream-500/70 dark:hover:bg-coffee-800/60 dark:hover:text-cream-300"
        }`}
      >
        {degraded && <AlertCircle className="h-3 w-3" aria-hidden="true" />}
        <span className="max-w-[14rem] truncate">{label}</span>
        {Icon && <Icon className="h-3 w-3" aria-hidden="true" />}
        {!Icon && !degraded && (
          <ChevronDown className="h-3 w-3 opacity-60" aria-hidden="true" />
        )}
      </button>
      {open && (
        <ChatLevelDialog
          state={state}
          rows={rows}
          busy={busy}
          defaultMatch={defaultMatch}
          notApplied={notApplied !== null}
          onPick={async (l) => {
            const ok = await pick(l);
            if (ok) setOpen(false);
            return ok;
          }}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  );
}
